import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import BreadCrumb from "../Controls/BreadCrumb";
import parse from "html-react-parser";
import { Form, FormActions } from "../Controls/Form";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import API from "@src/API.js";
import { decodeAgreementHtml } from "../Controls/agreementContent";
import { refreshSessionShell, useSessionShellQuery } from "../../query/useSessionShell";

export default function AgreementSignature() {
  const { id: idStr } = useParams();
  const agreementId = Number.parseInt(idStr, 10);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: shell } = useSessionShellQuery();
  const [agreement, setAgreement] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const list = shell?.agreements || [];
    const found = list.find(
      (a) => Number(a.AgreementID || a.agreementID) === agreementId
    );
    if (found) {
      setAgreement(found);
      return;
    }
    (async () => {
      const resp = await API.getAsync("/Agreement/" + agreementId);
      if (resp?.ok && resp.jsonData) {
        setAgreement({
          AgreementID: resp.jsonData.ID || agreementId,
          Name: resp.jsonData.Name,
          Content: resp.jsonData.Content,
          Required: true,
          DateAccepted: null,
        });
      } else {
        toast.error("Agreement not found");
      }
    })();
  }, [agreementId, shell?.agreements]);

  useEffect(() => {
    if (!agreement) return;
    const decoded = decodeAgreementHtml(agreement.Content || agreement.content || "");
    if (typeof decoded !== "string") {
      setContent("");
      return;
    }
    if (/<\/?[a-z][\s\S]*>/i.test(decoded)) {
      setContent(parse(decoded));
    } else {
      setContent(<pre className="agreementContent">{decoded}</pre>);
    }
  }, [agreement]);

  async function onDoneAsync() {
    if (!agreement || busy) return;
    setBusy(true);
    try {
      const payload = {
        ...agreement,
        AgreementID: agreement.AgreementID || agreementId,
        DateAccepted: DateTime.utc().toISO(),
      };
      const resp = await API.postAsync("/AgreementSignature", payload);
      if (resp && resp.ok) {
        toast.success("Agreement signed");
        await refreshSessionShell(queryClient);
        const returnTo = location.state?.returnTo;
        if (returnTo) navigate(returnTo);
        else navigate("/dashboard");
      } else {
        toast.error(resp?.jsonData?.message || "Could not sign agreement");
      }
    } finally {
      setBusy(false);
    }
  }

  if (!agreement) {
    return (
      <div className="pagebody">
        <p>Loading agreement…</p>
      </div>
    );
  }

  const accepted = !!(agreement.DateAccepted || agreement.dateAccepted);

  return (
    <div className="pagebody">
      <BreadCrumb
        props={[
          { route: "/dashboard", label: "Dashboard" },
          { label: agreement.Name || "Agreement" },
        ]}
      />
      <div className="tabbody">
        <h2>Agreement Details</h2>
        <p>
          Agreement Name: <span className="agreementName">{agreement.Name}</span>
          {agreement.Version || agreement.MajorVersion != null ? (
            <span className="fieldHint">
              {" "}
              (v
              {agreement.Version ||
                `${agreement.MajorVersion}.${agreement.MinorVersion ?? 0}`}
              )
            </span>
          ) : null}
        </p>
        <Form object={agreement} onUpdate={() => {}}>
          <div className="agreementContent">{content}</div>
          {accepted ? (
            <FormActions
              cancelLabel="Back"
              onCancelAsync={async () => navigate("/dashboard")}
            />
          ) : (
            <FormActions
              doneLabel={busy ? "Signing…" : "I Agree"}
              cancelLabel="Back"
              onSaveAsync={async () => {
                await onDoneAsync();
              }}
              onCancelAsync={async () => navigate("/dashboard")}
            />
          )}
        </Form>
      </div>
    </div>
  );
}
